import React, { useState, useEffect } from 'react';
import { Facebook } from 'lucide-react';
import { FacebookQRCode } from '../../types/qr-types';
import { t } from '../../i18n/translations';

interface FacebookFormProps {
  data: FacebookQRCode;
  onChange: (data: FacebookQRCode) => void;
  language: 'en' | 'zh';
}

const FacebookForm: React.FC<FacebookFormProps> = ({ data, onChange, language }) => {
  const [error, setError] = useState<string>('');

  const validateFacebookUrl = (url: string, type: 'profile' | 'share'): boolean => {
    if (type === 'profile') {
      return /^https?:\/\/(www\.)?facebook\.com\/[a-zA-Z0-9.]+$/.test(url);
    }
    return /^https?:\/\//.test(url);
  };

  const handleUrlChange = (url: string) => {
    if (!url) {
      setError('');
      onChange({ ...data, url });
      return;
    }

    if (!validateFacebookUrl(url, data.facebookType)) {
      setError(t('invalidFacebookUrl', language));
      onChange({ ...data, url });
      return;
    }

    // 校验通过时，清空错误
    setError('');
    onChange({ ...data, url });
  };

  const handleTypeChange = (type: 'profile' | 'share') => {
    onChange({ ...data, facebookType: type, url: '' });
    setError('');
  };

  // 首次渲染时，若未指定facebookType，自动默认选中'profile'
  useEffect(() => {
    if (!data.facebookType) {
      onChange({ ...data, facebookType: 'profile', url: data.url || '' });
    }
    // 只在首次渲染时运行
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  console.log('FacebookForm render', data);

  return (
    <div className="space-y-4">
      <div className="flex gap-4">
        <button
          onClick={() => handleTypeChange('profile')}
          className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
            data.facebookType === 'profile'
              ? 'bg-blue-600 text-white'
              : 'bg-white/5 text-gray-300 hover:bg-white/10'
          }`}
        >
          {t('facebookProfile', language)}
        </button>
        <button
          onClick={() => handleTypeChange('share')}
          className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
            data.facebookType === 'share'
              ? 'bg-blue-600 text-white'
              : 'bg-white/5 text-gray-300 hover:bg-white/10'
          }`}
        >
          {t('facebookShare', language)}
        </button>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          {data.facebookType === 'profile' ? t('facebookProfile', language) : t('facebookShare', language)}
        </label>
        <div className="relative">
          <Facebook className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
          <input
            type="url"
            value={data.url}
            onChange={(e) => handleUrlChange(e.target.value)}
            placeholder={
              data.facebookType === 'profile'
                ? t('facebookProfilePlaceholder', language)
                : t('facebookSharePlaceholder', language)
            }
            className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        {error && <p className="mt-2 text-sm text-red-400">{error}</p>}
      </div>
    </div>
  );
};

export default FacebookForm; 